import { useState, useRef, useEffect, useMemo } from "react";
import { canonicalizeBairro, areSameNeighborhood } from "../constants/neighborhoods";
import {
  THEMATIC_CATEGORIES,
  THEMATIC_CATEGORIES_ORDER,
} from "../constants/categories";

/**
 * Dropdown com seleção múltipla de anos (Exercício) para gráficos analíticos.
 *
 * @param {Object} props
 * @param {number[]} props.availableYears Lista de anos disponíveis
 * @param {number[]} props.selectedYears Lista de anos selecionados ([] = todos)
 * @param {Function} props.onChange Callback recebendo a nova lista de anos
 * @param {string} [props.label="Exercício"] Rótulo do filtro
 */
export function YearsFilterDropdown({
  availableYears = [],
  selectedYears = [],
  onChange,
  label = "Exercício",
  fullWidth = false,
  align = "left",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Fecha o dropdown ao clicar fora ou ao pressionar a tecla Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const hasFilter = selectedYears.length > 0;

  const toggleYear = (year) => {
    if (selectedYears.includes(year)) {
      onChange(selectedYears.filter((y) => y !== year));
    } else {
      onChange([...selectedYears, year].sort((a, b) => b - a));
    }
  };

  const handleSelectAll = () => {
    onChange([]);
  };

  const getButtonText = () => {
    if (!hasFilter) {
      return `${label}: Todos`;
    }
    if (selectedYears.length === 1) {
      return `${label}: ${selectedYears[0]}`;
    }
    if (selectedYears.length === availableYears.length) {
      return `${label}: Todos (${availableYears.length})`;
    }
    return `${label}: ${selectedYears.length} anos`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        width: fullWidth ? "100%" : "auto",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={
          hasFilter
            ? `Anos selecionados: ${[...selectedYears].sort((a, b) => a - b).join(", ")}`
            : "Exibindo todos os anos do período"
        }
        style={{
          height: 32,
          width: fullWidth ? "100%" : "auto",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: fullWidth ? "space-between" : "flex-start",
          gap: 6,
          padding: "0 10px",
          fontSize: "var(--text-label)",
          borderRadius: "var(--radius-sm)",
          border: hasFilter
            ? "1.5px solid var(--accent-primary)"
            : "1px solid var(--border-color)",
          background: hasFilter ? "var(--accent-light)" : "var(--bg-surface)",
          color: hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
          fontWeight: hasFilter ? 700 : 500,
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s ease",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: hasFilter ? "var(--accent-primary)" : "var(--text-muted)", flexShrink: 0 }}
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getButtonText()}
          </span>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            opacity: 0.7,
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Filtro de anos"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            zIndex: 150,
            minWidth: 210,
            maxWidth: "min(280px, calc(100vw - 32px))",
            maxHeight: "min(280px, calc(100vh - 160px))",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            padding: "8px 0 0 0",
            animation: "fadeIn 0.12s ease-out",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "4px 12px 8px 12px",
              borderBottom: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "var(--text-micro)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-wide)",
                color: "var(--text-muted)",
              }}
            >
              Exercício
            </span>
            {hasFilter ? (
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  color: "var(--accent-primary)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Limpar seleção
              </button>
            ) : (
              <span style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)" }}>
                Todos ativos
              </span>
            )}
          </div>

          <div style={{ flex: 1, maxHeight: 160, overflowY: "auto", padding: "4px 0" }}>
            {availableYears.map((year) => {
              const isChecked = selectedYears.includes(year);
              return (
                <label
                  key={year}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    fontSize: "var(--text-label)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    userSelect: "none",
                    background: isChecked ? "var(--accent-light)" : "transparent",
                    transition: "background 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isChecked) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                  }}
                  onMouseLeave={(e) => {
                    if (!isChecked) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleYear(year)}
                    style={{
                      cursor: "pointer",
                      width: 14,
                      height: 14,
                      accentColor: "var(--accent-primary)",
                    }}
                  />
                  <span style={{ fontWeight: isChecked ? 700 : 500 }}>
                    Ano {year}
                  </span>
                </label>
              );
            })}
          </div>

          <div
            style={{
              padding: "6px 12px",
              borderTop: "1px solid var(--border-color)",
              fontSize: "var(--text-micro)",
              color: "var(--text-muted)",
              textAlign: "right",
              flexShrink: 0,
              boxSizing: "border-box",
              lineHeight: 1.3,
            }}
          >
            {hasFilter
              ? `${selectedYears.length} ano(s) filtrado(s)`
              : "Exibindo histórico completo"}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Dropdown com seleção múltipla e campo de busca rápida de Parlamentares para gráficos analíticos.
 *
 * @param {Object} props
 * @param {string[]} props.availableAuthors Lista de parlamentares disponíveis
 * @param {string[]|string|null} [props.selectedAuthors=[]] Lista de autores selecionados ([] = todos)
 * @param {string|null} [props.selectedAuthor] Compatibilidade com autor único
 * @param {Function} props.onChange Callback recebendo a nova lista de autores (string[])
 * @param {string} [props.label="Parlamentar"] Rótulo do filtro
 */
export function AuthorFilterDropdown({
  availableAuthors = [],
  selectedAuthors = [],
  selectedAuthor = null,
  onChange,
  label = "Parlamentar",
  fullWidth = false,
  align = "left",
  singleSelect = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Normaliza a lista de autores selecionados para garantir array
  const activeAuthors =
    Array.isArray(selectedAuthors) && selectedAuthors.length > 0
      ? selectedAuthors
      : selectedAuthor
      ? [selectedAuthor]
      : Array.isArray(selectedAuthors)
      ? selectedAuthors
      : [];

  // Foco no input de busca ao abrir o dropdown
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else {
      setSearchTerm("");
    }
  }, [isOpen]);

  // Fecha o dropdown ao clicar fora ou com a tecla Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const hasFilter = activeAuthors.length > 0;

  const filteredAuthors = availableAuthors.filter((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  const toggleAuthor = (name) => {
    if (singleSelect) {
      if (activeAuthors.includes(name)) {
        onChange(null);
      } else {
        onChange(name);
      }
      setIsOpen(false);
      return;
    }
    if (activeAuthors.includes(name)) {
      onChange(activeAuthors.filter((a) => a !== name));
    } else {
      onChange([...activeAuthors, name]);
    }
  };

  const handleClear = () => {
    if (singleSelect) {
      onChange(null);
      setIsOpen(false);
      return;
    }
    onChange([]);
  };

  const getButtonText = () => {
    if (!hasFilter) {
      return `${label}: Todos`;
    }
    if (activeAuthors.length === 1) {
      return `${label}: ${activeAuthors[0]}`;
    }
    return `${label}: ${activeAuthors.length} selecionados`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        width: fullWidth ? "100%" : "auto",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={
          hasFilter
            ? `Parlamentares selecionados (${activeAuthors.length}): ${activeAuthors.join(", ")}`
            : "Todos os parlamentares do período"
        }
        style={{
          height: 32,
          width: fullWidth ? "100%" : "auto",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: fullWidth ? "space-between" : "flex-start",
          gap: 6,
          padding: "0 10px",
          fontSize: "var(--text-label)",
          borderRadius: "var(--radius-sm)",
          border: hasFilter
            ? "1.5px solid var(--accent-primary)"
            : "1px solid var(--border-color)",
          background: hasFilter ? "var(--accent-light)" : "var(--bg-surface)",
          color: hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
          fontWeight: hasFilter ? 700 : 500,
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s ease",
          maxWidth: fullWidth ? "none" : 240,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            style={{ color: hasFilter ? "var(--accent-primary)" : "var(--text-muted)", flexShrink: 0 }}
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getButtonText()}
          </span>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            opacity: 0.7,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Filtro de parlamentares"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            zIndex: 150,
            width: 280,
            maxWidth: "min(280px, calc(100vw - 32px))",
            maxHeight: "min(280px, calc(100vh - 160px))",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            padding: "8px 0 0 0",
            animation: "fadeIn 0.12s ease-out",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* Campo de Busca Rápida */}
          <div style={{ padding: "0 10px 8px 10px", flexShrink: 0 }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar vereador..."
                style={{
                  width: "100%",
                  height: 30,
                  padding: "0 26px 0 8px",
                  fontSize: "var(--text-label)",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-primary, #fff)",
                  color: "var(--text-primary)",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    background: "transparent",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: "var(--text-label)",
                    padding: 0,
                    lineHeight: 1,
                  }}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Cabeçalho de Controle / Ação Rápida */}
          <div
            style={{
              padding: "4px 12px 8px 12px",
              borderTop: "1px solid var(--border-color)",
              borderBottom: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "var(--text-micro)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-wide)",
                color: "var(--text-muted)",
              }}
            >
              Parlamentares ({availableAuthors.length})
            </span>
            {hasFilter ? (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  color: "var(--accent-primary)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Limpar seleção
              </button>
            ) : (
              <span style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)" }}>
                Todos ativos
              </span>
            )}
          </div>

          {/* Lista de Parlamentares */}
          <div style={{ flex: 1, maxHeight: 160, overflowY: "auto", padding: "4px 0" }}>
            {singleSelect && (
              <div
                onClick={handleClear}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 12px",
                  fontSize: "var(--text-label)",
                  color: !hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
                  fontWeight: !hasFilter ? 700 : 500,
                  background: !hasFilter ? "var(--accent-light)" : "transparent",
                  cursor: "pointer",
                  transition: "background 0.1s ease",
                }}
                onMouseEnter={(e) => {
                  if (hasFilter) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                }}
                onMouseLeave={(e) => {
                  if (hasFilter) e.currentTarget.style.background = "transparent";
                }}
              >
                <span>Todos os parlamentares</span>
                {!hasFilter && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            )}

            {filteredAuthors.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  fontSize: "var(--text-label)",
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                Nenhum vereador encontrado
              </div>
            ) : (
              filteredAuthors.map((authorName) => {
                const isChecked = activeAuthors.includes(authorName);

                if (singleSelect) {
                  return (
                    <div
                      key={authorName}
                      onClick={() => toggleAuthor(authorName)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 12px",
                        fontSize: "var(--text-label)",
                        color: isChecked ? "var(--accent-primary)" : "var(--text-primary)",
                        fontWeight: isChecked ? 700 : 500,
                        background: isChecked ? "var(--accent-light)" : "transparent",
                        cursor: "pointer",
                        transition: "background 0.1s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isChecked) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                      }}
                      onMouseLeave={(e) => {
                        if (!isChecked) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {authorName}
                      </span>
                      {isChecked && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  );
                }

                return (
                  <label
                    key={authorName}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                    fontSize: "var(--text-label)",
                    color: "var(--text-primary)",
                      cursor: "pointer",
                      userSelect: "none",
                      background: isChecked ? "var(--accent-light)" : "transparent",
                      transition: "background 0.1s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                    }}
                    onMouseLeave={(e) => {
                      if (!isChecked) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleAuthor(authorName)}
                      style={{
                        cursor: "pointer",
                        width: 14,
                        height: 14,
                        accentColor: "var(--accent-primary)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontWeight: isChecked ? 700 : 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {authorName}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          {/* Rodapé com Resumo de Selecionados */}
          <div
            style={{
              padding: "6px 12px",
              borderTop: "1px solid var(--border-color)",
              fontSize: "var(--text-micro)",
              color: "var(--text-muted)",
              textAlign: "right",
              flexShrink: 0,
              boxSizing: "border-box",
              lineHeight: 1.3,
            }}
          >
            {hasFilter
              ? `${activeAuthors.length} parlamentar(es) filtrado(s)`
              : "Exibindo todos os mandatos"}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Dropdown com campo de busca e seleção de Categoria Temática para o painel analítico.
 * Destaque visual azul (ativo) idêntico aos filtros de Parlamentar e Exercício.
 *
 * @param {Object} props
 * @param {string|null} [props.selectedCategory=null] Chave da categoria ativa (null = todas)
 * @param {Function} props.onChange Callback recebendo a nova categoria selecionada (string|null)
 * @param {string} [props.label="Categoria"] Rótulo do filtro
 */
export function CategoryFilterDropdown({
  selectedCategory = null,
  onChange,
  label = "Categoria",
  fullWidth = false,
  align = "left",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else {
      setSearchTerm("");
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const hasFilter = Boolean(selectedCategory);
  const activeCatObj = selectedCategory ? THEMATIC_CATEGORIES[selectedCategory] : null;

  const filteredCategories = THEMATIC_CATEGORIES_ORDER.filter((key) => {
    const cat = THEMATIC_CATEGORIES[key];
    if (!cat) return false;
    return (cat.label || key).toLowerCase().includes(searchTerm.toLowerCase().trim());
  });

  const handleSelect = (key) => {
    if (selectedCategory === key) {
      onChange(null);
    } else {
      onChange(key);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const getButtonText = () => {
    if (!hasFilter || !activeCatObj) {
      return `${label}: Todas`;
    }
    return `${label}: ${activeCatObj.label}`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        width: fullWidth ? "100%" : "auto",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={
          hasFilter
            ? `Tema ativo: ${activeCatObj?.label || selectedCategory}`
            : "Exibindo todas as categorias temáticas"
        }
        style={{
          height: 32,
          width: fullWidth ? "100%" : "auto",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: fullWidth ? "space-between" : "flex-start",
          gap: 6,
          padding: "0 10px",
          fontSize: "var(--text-label)",
          borderRadius: "var(--radius-sm)",
          border: hasFilter
            ? "1.5px solid var(--accent-primary)"
            : "1px solid var(--border-color)",
          background: hasFilter ? "var(--accent-light)" : "var(--bg-surface)",
          color: hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
          fontWeight: hasFilter ? 700 : 500,
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s ease",
          maxWidth: fullWidth ? "none" : 260,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
          {hasFilter && activeCatObj ? (
            <div
              style={{
                width: 15,
                height: 15,
                borderRadius: "var(--radius-xs)",
                background: activeCatObj.solid || activeCatObj.bgColor,
                color: activeCatObj.onSolid || "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                className="geom-svg-icon"
                style={{ width: 9, height: 9, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                dangerouslySetInnerHTML={{ __html: activeCatObj.icon }}
              />
            </div>
          ) : (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              style={{ color: "var(--text-muted)", flexShrink: 0 }}
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getButtonText()}
          </span>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            opacity: 0.7,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Filtro de categorias temáticas"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            zIndex: 150,
            width: 280,
            maxWidth: "min(280px, calc(100vw - 32px))",
            maxHeight: "min(280px, calc(100vh - 160px))",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            padding: "8px 0 0 0",
            animation: "fadeIn 0.12s ease-out",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* Campo de Busca Rápida */}
          <div style={{ padding: "0 10px 8px 10px", flexShrink: 0 }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar tema..."
                style={{
                  width: "100%",
                  height: 30,
                  padding: "0 26px 0 8px",
                  fontSize: "var(--text-label)",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-primary, #fff)",
                  color: "var(--text-primary)",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    background: "transparent",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: "var(--text-label)",
                    padding: 0,
                    lineHeight: 1,
                  }}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Cabeçalho de Controle / Ação Rápida */}
          <div
            style={{
              padding: "4px 12px 8px 12px",
              borderTop: "1px solid var(--border-color)",
              borderBottom: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "var(--text-micro)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-wide)",
                color: "var(--text-muted)",
              }}
            >
              Categorias (15)
            </span>
            {hasFilter ? (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  color: "var(--accent-primary)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Limpar seleção
              </button>
            ) : (
              <span style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)" }}>
                Todas ativas
              </span>
            )}
          </div>

          {/* Lista de Categorias com Ícone e Seleção */}
          <div style={{ flex: 1, maxHeight: 160, overflowY: "auto", padding: "4px 0" }}>
            {filteredCategories.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  fontSize: "var(--text-label)",
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                Nenhuma categoria encontrada
              </div>
            ) : (
              filteredCategories.map((key) => {
                const cat = THEMATIC_CATEGORIES[key];
                const isSelected = selectedCategory === key;

                return (
                  <div
                    key={key}
                    onClick={() => handleSelect(key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                      fontSize: "var(--text-label)",
                      color: isSelected ? "var(--accent-primary)" : "var(--text-primary)",
                      cursor: "pointer",
                      userSelect: "none",
                      background: isSelected ? "var(--accent-light)" : "transparent",
                      transition: "background 0.1s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Ícone da Categoria */}
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "var(--radius-sm)",
                        background: cat.solid || cat.bgColor,
                        color: cat.onSolid || "#ffffff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        className="geom-svg-icon"
                        style={{ width: 11, height: 11 }}
                        dangerouslySetInnerHTML={{ __html: cat.icon }}
                      />
                    </div>

                    <span
                      style={{
                        flex: 1,
                        fontWeight: isSelected ? 700 : 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cat.label}
                    </span>

                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Rodapé com Resumo */}
          <div
            style={{
              padding: "6px 12px",
              borderTop: "1px solid var(--border-color)",
              fontSize: "var(--text-micro)",
              color: "var(--text-muted)",
              textAlign: "right",
              flexShrink: 0,
              boxSizing: "border-box",
              lineHeight: 1.3,
            }}
          >
            {hasFilter
              ? `Tema ativo: ${activeCatObj?.label || selectedCategory}`
              : "Exibindo todas as 15 categorias"}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Dropdown com seleção de Bairro e campo de busca rápida para filtros.
 *
 * @param {Object} props
 * @param {string[]} props.availableBairros Lista de bairros disponíveis
 * @param {string|null} [props.selectedBairro=null] Bairro selecionado (null = todos)
 * @param {Function} props.onChange Callback recebendo o novo bairro (string|null)
 * @param {string} [props.label="Bairro"] Rótulo do filtro
 * @param {boolean} [props.fullWidth=false] Ocupar 100% da largura do container
 * @param {string} [props.align="left"] Alinhamento do popover ('left' | 'right')
 */
export function BairroFilterDropdown({
  availableBairros = [],
  selectedBairro = null,
  onChange,
  label = "Bairro",
  fullWidth = false,
  align = "left",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else {
      setSearchTerm("");
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Normaliza (Aa), deduplica e ordena alfabeticamente os bairros disponíveis
  const normalizedBairros = useMemo(() => {
    const bMap = new Map();
    (availableBairros || []).forEach((name) => {
      const formatted = canonicalizeBairro(name);
      if (formatted && formatted !== "Não informado" && formatted.trim()) {
        bMap.set(formatted.toLowerCase(), formatted);
      }
    });
    return Array.from(bMap.values()).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [availableBairros]);

  const hasFilter = Boolean(selectedBairro);

  const filteredBairros = normalizedBairros.filter((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  const handleSelect = (bairro) => {
    const canon = canonicalizeBairro(bairro);
    if (
      selectedBairro &&
      (selectedBairro.toLowerCase() === canon.toLowerCase() ||
        areSameNeighborhood(selectedBairro, canon))
    ) {
      onChange(null);
    } else {
      onChange(canon);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
  };

  const getButtonText = () => {
    if (!hasFilter) {
      return `${label}: Todos`;
    }
    return `${label}: ${canonicalizeBairro(selectedBairro)}`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        width: fullWidth ? "100%" : "auto",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={hasFilter ? `Bairro selecionado: ${canonicalizeBairro(selectedBairro)}` : "Todos os bairros"}
        style={{
          height: 32,
          width: fullWidth ? "100%" : "auto",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: fullWidth ? "space-between" : "flex-start",
          gap: 6,
          padding: "0 10px",
          fontSize: "var(--text-label)",
          borderRadius: "var(--radius-sm)",
          border: hasFilter
            ? "1.5px solid var(--accent-primary)"
            : "1px solid var(--border-color)",
          background: hasFilter ? "var(--accent-light)" : "var(--bg-surface)",
          color: hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
          fontWeight: hasFilter ? 700 : 500,
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s ease",
          maxWidth: fullWidth ? "none" : 240,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, overflow: "hidden" }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            style={{ color: hasFilter ? "var(--accent-primary)" : "var(--text-muted)", flexShrink: 0 }}
          >
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getButtonText()}
          </span>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            flexShrink: 0,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            opacity: 0.7,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Filtro de bairros"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            zIndex: 150,
            width: 270,
            maxWidth: "min(280px, calc(100vw - 32px))",
            maxHeight: "min(280px, calc(100vh - 160px))",
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            padding: "8px 0 0 0",
            animation: "fadeIn 0.12s ease-out",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* Campo de Busca Rápida */}
          <div style={{ padding: "0 10px 8px 10px", flexShrink: 0 }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar bairro..."
                style={{
                  width: "100%",
                  height: 30,
                  padding: "0 26px 0 8px",
                  fontSize: "var(--text-label)",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-primary, #fff)",
                  color: "var(--text-primary)",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    background: "transparent",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: "var(--text-label)",
                    padding: 0,
                    lineHeight: 1,
                  }}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Cabeçalho de Controle / Ação Rápida */}
          <div
            style={{
              padding: "4px 12px 8px 12px",
              borderTop: "1px solid var(--border-color)",
              borderBottom: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "var(--text-micro)",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-wide)",
                color: "var(--text-muted)",
              }}
            >
              Bairros ({normalizedBairros.length})
            </span>
            {hasFilter ? (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  color: "var(--accent-primary)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Limpar seleção
              </button>
            ) : (
              <span style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)" }}>
                Todos ativos
              </span>
            )}
          </div>

          {/* Lista de Bairros com Seleção */}
          <div style={{ flex: 1, maxHeight: 160, overflowY: "auto", padding: "4px 0" }}>
            {/* Opção Todos */}
            <div
              onClick={handleClear}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 12px",
                fontSize: "var(--text-label)",
                color: !hasFilter ? "var(--accent-primary)" : "var(--text-primary)",
                fontWeight: !hasFilter ? 700 : 500,
                background: !hasFilter ? "var(--accent-light)" : "transparent",
                cursor: "pointer",
                transition: "background 0.1s ease",
              }}
              onMouseEnter={(e) => {
                if (hasFilter) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
              }}
              onMouseLeave={(e) => {
                if (hasFilter) e.currentTarget.style.background = "transparent";
              }}
            >
              <span>Todos os bairros</span>
              {!hasFilter && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>

            {filteredBairros.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  fontSize: "var(--text-label)",
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                Nenhum bairro encontrado
              </div>
            ) : (
              filteredBairros.map((bairroName) => {
                const isSelected =
                  selectedBairro &&
                  (selectedBairro.toLowerCase() === bairroName.toLowerCase() ||
                    areSameNeighborhood(selectedBairro, bairroName));
                return (
                  <div
                    key={bairroName}
                    onClick={() => handleSelect(bairroName)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 12px",
                      fontSize: "var(--text-label)",
                      color: isSelected ? "var(--accent-primary)" : "var(--text-primary)",
                      fontWeight: isSelected ? 700 : 500,
                      background: isSelected ? "var(--accent-light)" : "transparent",
                      cursor: "pointer",
                      transition: "background 0.1s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--bg-muted, rgba(0,0,0,0.03))";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {bairroName}
                    </span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Rodapé */}
          <div
            style={{
              padding: "6px 12px",
              borderTop: "1px solid var(--border-color)",
              fontSize: "var(--text-micro)",
              color: "var(--text-muted)",
              textAlign: "right",
              flexShrink: 0,
              boxSizing: "border-box",
              lineHeight: 1.3,
            }}
          >
            {hasFilter
              ? `Bairro ativo: ${canonicalizeBairro(selectedBairro)}`
              : "Exibindo todos os bairros"}
          </div>
        </div>
      )}
    </div>
  );
}
